import { useEffect, useState, useMemo, useCallback } from "react";
import { withRouter, Link, useParams } from "react-router-dom";
import "./ProductDetails.scss";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import { getAllFolders } from "../../actions/folders-actions/actions";
import {
  getFilterAttributes,
  getProduct,
  getProductsFiltered,
} from "../../actions/products-actions/actions";
import axiosInstance from "../../config/axios-instance";

import ProductSkeleton from "./ProductSkeleton";
import DenimProduct from "./DenimProduct";
import KnitProduct from "./KnitProduct";
import ProductFooter from "./ProductFooter";
import ProductImage from "./ProductImage";
import MixProduct from "./MixProduct";

export interface IProductDetailsProps {}

const ProductDetails = (props: IProductDetailsProps) => {
  const [totalLook, setTotalLook] = useState<any>(null);
  const [isLoadingTotalLook, setIsLoadingTotalLook] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [editMode, setEditMode] = useState(false);
  const [mode, setMode] = useState("add_to_collection");
  const [visible, setVisible] = useState(false);
  
  const folders = useSelector((state: RootStateOrAny) => state.folders);
  const drawers = useSelector((state: RootStateOrAny) => state.drawers);
  const { isLoading, selectedProduct } = useSelector(
    (state: RootStateOrAny) => state.products
  );

  const dispatch = useDispatch();
  const params: any = useParams();

  // Resize handler
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Memoized fetch function
  const fetchPageData = useCallback(async () => {
    const token = localStorage.getItem("token");
    setIsLoadingTotalLook(true);

    // Dispatch Redux actions immediately
    dispatch(getAllFolders());
    dispatch(getProduct(params.productId));

    try {
      // Track Visit
      axiosInstance({
        method: "post",
        url: `products/track-visit`,
        data: { product_id: params.productId },
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {}); // Non-critical, ignore errors

      // Get Total Look
      const res = await axiosInstance({
        method: "get",
        url: `/products/${params.productId}/total-look`,
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res?.data?.data) {
        setTotalLook(res.data.data);
      }
    } catch (err: any) {
      console.error("Error loading product details data", err);
    } finally {
      setIsLoadingTotalLook(false);
    }
  }, [params.productId, dispatch]);

  // Fetch data when productId changes
  useEffect(() => {
    if (params.productId) {
      fetchPageData();
    }
  }, [params.productId, fetchPageData]);

  // Derived state for similar products using useMemo
  const similarProducts = useMemo(() => {
    if (!selectedProduct?.fabrics) return [];
    
    const allSimilar = selectedProduct.fabrics.flatMap((obj: any) =>
      (obj.products || []).filter(
        (product: any) => product.id !== selectedProduct.id
      )
    );

    // Filter duplicates by ID
    const uniqueMap = new Map();
    allSimilar.forEach((product: any) => {
      if (product?.id) uniqueMap.set(product.id, product);
    });

    // Limit to 12 products for performance
    return Array.from(uniqueMap.values()).slice(0, 12);
  }, [selectedProduct?.id, selectedProduct?.fabrics]);

  // Dependent Redux Dispatches for filters
  useEffect(() => {
    if (selectedProduct?.id) {
      dispatch(
        getProductsFiltered(
          selectedProduct.fabric_type,
          selectedProduct.categories?.[0]?.id,
          selectedProduct.gender_id
        )
      );
      dispatch(getFilterAttributes(selectedProduct.fabric_type, "17", "1"));
    }
  }, [selectedProduct?.id, dispatch]);

  const oneProduct = selectedProduct;

  // Show skeleton only during initial load
  if (isLoading && !oneProduct) {
    return <ProductSkeleton oneProduct={oneProduct} />;
  }

  // Product not found state
  if (!oneProduct && !isLoading) {
    return (
      <div className="text-center mt-5">
        <h2>Product not found</h2>
        <Link to="/products">Back to products</Link>
      </div>
    );
  }

  // Render product details
  const renderProductComponent = () => {
    if (!oneProduct) return null;

    const commonProps = {
      oneProduct,
      editMode,
      setEditMode,
      totalLook,
      similarProducts,
      isLoadingTotalLook,
      isLoadingSimilar: false,
    };

    switch (oneProduct.fabric_type) {
      case "knit":
        return <KnitProduct key="knit_view" {...commonProps} />;
      case "denim":
        return <DenimProduct key="denim_view" {...commonProps} />;
      default:
        return <MixProduct key="mix_view" {...commonProps} />;
    }
  };

  return (
    <div className="product_details_block container-fluid">
      <div className="product_details_contanier row">
        <div className="product_images_wrapper col-6">
          <ProductImage
            oneProduct={oneProduct}
            width={width}
            setVisible={setVisible}
            visible={visible}
          />
        </div>
        <div className="product_details_infos_scroll col-6">
          <div
            className="product_details_infos"
            style={{ paddingTop: editMode ? "20px" : "0px" }}
          >
            {renderProductComponent()}
          </div>
          {!editMode && (
            <ProductFooter
              isLoadingNext={false}
              width={width}
              oneProduct={oneProduct}
            />
          )}
        </div>
      </div>

      {drawers.openBottom && (
        <BottomDrawer folders={folders} mode={mode} setMode={setMode} />
      )}
    </div>
  );
};

export default withRouter(ProductDetails);
